import { Form, formProviderHOC } from '@sravni/cosago-react-library/lib/components';
import type { FormComponentProps } from '@sravni/cosago-react-library/lib/types';
import { useIsMobile } from '@sravni/react-utils';
import type { FC } from 'react';
import React, { useEffect } from 'react';

import { LABELS } from 'constants/labels';

import { useAppSelector } from 'shared/lib';
import { sendEventShowStep } from 'shared/lib/sendGAEvents';
import { useCommonFormBodyHooks } from 'shared/lib/useCommonFormBodyHooks';
import type { TCommonAdditionalFormProps } from 'shared/types';
import { FormStickyButton } from 'shared/ui';
import { AdaptivePopover } from 'shared/ui/AdaptivePopover';
import { FormBlockLabel } from 'shared/ui/FormBlockLabel';
import { FormWithControlledNavigation } from 'shared/ui/FormWithControlledNavigation';

import { useSendFieldErrorAnalyticsEvent } from 'entities/creditForm/lib/useSendFieldErrorAnalyticsEvent';
import { selectAllFormFields, type TPassportFormFields } from 'src/client/entities/creditForm';
import { useDevFillMockDataTool } from 'src/client/entities/creditForm/lib/useDevFillMockDataTool';

import { PassportInfoHintTexts, PassportInfoTexts } from 'features/UpdatePassportInfo';
import {
  useResidenceLocationRouteLoader,
  useRegistrationLocationRouteLoader,
} from 'features/UpdatePassportInfo/lib/useAddressLocationRouteLoader';
import { useAutofillRegionFromResidence } from 'features/UpdatePassportInfo/lib/useAutofillRegionFromResidence';
import { useIsShowResidenceFields } from 'features/UpdatePassportInfo/lib/useIsShowResidenceFields';
import { useUpdateResidenceFields } from 'features/UpdatePassportInfo/lib/useUpdateResidenceFields';
import { FormFieldsNavigationSequence } from 'features/UpdatePassportInfo/PassportInfo.config';

import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';
import { useRegionLocationRouteLoader } from '../../lib/useRegionLocationRouteLoader';
import { useUpdateGender } from '../../lib/useUpdateGender';

type TPassportInfoFormView = TCommonAdditionalFormProps & {
  shouldRestoreAdditionalData: boolean;
};

type Props = FormComponentProps<TPassportFormFields, TPassportInfoFormView>;

const PassportInfoFormView: FC<Props> = ({
  isDialog,
  onSubmit,
  NAMES,
  submitButtonText,
  FieldConstructor,
  isLoading,
  additionalProps,
}) => {
  const { setHeader, isFormForceOpened, formId, isAnyFormStepOpenedInPopup } = additionalProps || {};
  const allFormFields = useAppSelector(selectAllFormFields);
  const isMobile = useIsMobile();

  const [title, subtitle] = useAccordionHeaderParams(allFormFields, {}, false);

  const { controls } = useCommonFormBodyHooks({
    currentControls: FormFieldsNavigationSequence,
    isFormForceOpened,
    headerData: {
      title,
      subtitle,
    },
    setHeader,
  });

  useEffect(() => {
    sendEventShowStep(3, 'Паспорт');
  }, []);

  useUpdateGender();
  useRegionLocationRouteLoader();
  useResidenceLocationRouteLoader();
  useRegistrationLocationRouteLoader();

  useSendFieldErrorAnalyticsEvent<TPassportFormFields>(3, PassportInfoTexts);
  useDevFillMockDataTool<TPassportFormFields>({
    type: 'passportData',
    stepLabel: 'Паспорт',
  });

  useUpdateResidenceFields();
  useAutofillRegionFromResidence();

  const isShowResidenceFields = useIsShowResidenceFields();

  const isStickyButton = !isDialog && isMobile && !isAnyFormStepOpenedInPopup;

  return (
    <FormWithControlledNavigation
      formId={formId}
      navigationControls={controls}
      onSubmit={onSubmit}
      controlElement={FieldConstructor}
      isDialog={isDialog}
      isLoading={isLoading}
      submitButtonText={submitButtonText}
      withSubmitButtons={!isStickyButton}
    >
      <Form.Block>
        <Form.Row colWidths={[12]}>
          <FieldConstructor type={NAMES.fullName} />
        </Form.Row>
        <Form.Row>
          <FieldConstructor type={NAMES.passportNumber} />
          <FieldConstructor type={NAMES.passportIssueDate} />
          <FieldConstructor type={NAMES.unitCode} />
        </Form.Row>
        <Form.Row colWidths={[12]}>
          <AdaptivePopover
            placement="top"
            content={PassportInfoHintTexts.passportIssuedBy}
          >
            <FieldConstructor type={NAMES.passportIssuedBy} />
          </AdaptivePopover>
        </Form.Row>
        <Form.Row colWidths={[4, 8]}>
          <FieldConstructor type={NAMES.birthDate} />
          <AdaptivePopover
            placement="top"
            content={PassportInfoHintTexts.birthPlace}
          >
            <FieldConstructor type={NAMES.birthPlace} />
          </AdaptivePopover>
        </Form.Row>
        <FormBlockLabel>{LABELS.RegistrationAddress}</FormBlockLabel>
        <Form.Row colWidths={[8, 4]}>
          <FieldConstructor type={NAMES.registrationAddress} />
          <FieldConstructor type={NAMES.registrationAddressFlat} />
        </Form.Row>
        <Form.Row>
          <FieldConstructor type={NAMES.registrationDate} />
        </Form.Row>
        <Form.Row colWidths={[8]}>
          <FieldConstructor type={NAMES.residenceAddressIsRegistration} />
        </Form.Row>
        {isShowResidenceFields && (
          <Form.Row colWidths={[8, 4]}>
            <FieldConstructor type={NAMES.residenceAddress} />
            <FieldConstructor type={NAMES.residenceAddressFlat} />
          </Form.Row>
        )}
        <FormBlockLabel>{LABELS.RegionLabel}</FormBlockLabel>
        <Form.Row colWidths={[8]}>
          <FieldConstructor type={NAMES.region} />
        </Form.Row>
      </Form.Block>

      <FormStickyButton
        onClick={onSubmit}
        enabled={isStickyButton}
      >
        {submitButtonText}
      </FormStickyButton>
    </FormWithControlledNavigation>
  );
};

export const PassportInfoViewHoc = formProviderHOC<TPassportFormFields, TPassportInfoFormView>(PassportInfoFormView);
